import { Collapse, Grid, debounce } from '@mui/material';
import { FacilityFilterSpecs } from './FacilityFilterUtils';
import React, { useCallback, useEffect, useRef } from 'react';
import { getCitiesAndZipsByStates } from 'app/services/SharedServices/SharedServices';
import {
  selectFilters,
  selectGridFiltersExpanded,
  selectGridState,
} from 'app/components/Common/Grid/GridStateManagement/GridState.selectors';
import { useDispatch, useSelector } from 'react-redux';
import { IStateCityZip } from 'app/models/Candidate/IStateCityZip';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import { SearchType } from 'app/models/GlobalSearch/GlobalSearch';
import { getAllCitiesAndZips } from 'app/components/GlobalSearch/Candidate/FilterUtils';
import { getMultiSelectOptions } from 'app/components/GlobalSearch/helper';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { lookupOptionSelector } from 'store/redux-store/lookup/lookup.selectors';
import { ILookupOptions } from 'store/redux-store/lookup/initialState';
import { CustomFilter } from 'app/ComponentLibrary/Filter/CustomFilter';
import { selectPlacementDetails } from 'store/redux-store/placement-details/selectors';
import { useStyles } from '../../Helper/Styles';
import { initialFacilityChips } from '../../Helper/Types';

export const PlacementChangeFacilityFilter = props => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { reset, getValues } = useFormContext();
  const gridStateFilters = useSelector(selectFilters);
  const gridState = useSelector(selectGridState);
  const loadedData = useSelector(lookupOptionSelector) as ILookupOptions;
  const placementDetails = useSelector(selectPlacementDetails);
  const filtersExpanded = useSelector(selectGridFiltersExpanded);
  const allCitiesandZipsData = useRef<IStateCityZip[]>();

  const handleChange = () => {
    dispatch(gridStateActions.setFiltersExpanded(!filtersExpanded));
  };

  const gridSpecs = useRef(
    new FacilityFilterSpecs(
      {
        divisionOpts: [],
        facilityStatusesOpts: [],
        compactStatesOpts: [],
        statesOpts: [],
        accountManagerOpts: [],
        cityOptions: [],
      },
      gridStateFilters,
    ),
  );
  const [fields, setFields] = React.useState(gridSpecs.current.getFacilityFilterSpecs());

  const cityStateZipHandler = (selectedStates: string[], selectedCities?: string[]) => {
    getCitiesAndZipsByStates({
      states: selectedStates,
      cities: selectedCities ?? [],
    }).then((res: IStateCityZip[]) => {
      selectedCities
        ? (gridSpecs.current.filterSpecs.cityOptions = res[0].cities)
        : (gridSpecs.current.filterSpecs.zipOptions = res[0].zips);
      const fieldsSchema = gridSpecs.current.getFacilityFilterSpecs();
      setFields(fieldsSchema);
    });
  };

  const updateGridState = useCallback(
    async (selectedStates: string[], selectedCities: string[], gridStateFilters: any) => {
      if (!gridStateFilters || !allCitiesandZipsData.current) return;
      let filteredCities: IStateCityZip[];
      if (!selectedStates.length) filteredCities = allCitiesandZipsData.current;
      else {
        debounce(() => cityStateZipHandler(selectedStates), 1500);
      }
      gridSpecs.current.filterSpecs.cityOptions = getAllCitiesAndZips(filteredCities).cities;
      gridSpecs.current.gridStateFilters = gridStateFilters;
      const fieldsSchema = gridSpecs.current.getFacilityFilterSpecs();
      setFields(fieldsSchema);
    },
    [],
  );

  const fetchFacilityFilterSpecs = async () => {
    const divisionOpts = getMultiSelectOptions(loadedData?.divisionOptions || []);
    const facilityStatusesOpts = getMultiSelectOptions(loadedData?.facilityStatusOptions || []);
    const statesLookups = loadedData?.stateOptions;
    const states = getMultiSelectOptions(statesLookups || []);
    const compactStatesOpts = loadedData?.compactStateOptions;
    const compactStates = compactStatesOpts?.map(item => {
      const stateAbbrs = item.name?.split(',');
      return {
        name: item?.value?.replace('?', "'")?.replace('Only ', '')?.replace('LIC', 'Licence'),
        value: states?.filter(s => stateAbbrs.includes(s.value)),
        isCompactOpt: true,
      };
    });
    const statesOpts = getMultiSelectOptions([...compactStates, ...statesLookups] || []);
    const accountManagerOpts = getMultiSelectOptions(loadedData?.accountManagerOptions || []);

    allCitiesandZipsData.current = (await getCitiesAndZipsByStates({
      states: [statesLookups?.[0]],
      cities: undefined,
    })) as IStateCityZip[];
    const AllCitiesAndZips = getAllCitiesAndZips(allCitiesandZipsData.current);
    const cityOpts = AllCitiesAndZips?.cities;

    gridSpecs.current.filterSpecs = {
      divisionOpts,
      facilityStatusesOpts,
      compactStatesOpts,
      statesOpts,
      accountManagerOpts,
      cityOptions: cityOpts,
    };
    const fieldsSchema = gridSpecs.current.getFacilityFilterSpecs();
    setFields(fieldsSchema);
  };

  const defaultAppliedFilters = {
    ...initialFacilityChips,
    divisionIds: [
      {
        id: placementDetails?.header?.facility?.amnDivisionTypeId,
        name: placementDetails?.header?.facility?.amnDivisionType,
        value: placementDetails?.header?.facility?.amnDivisionTypeId.toString(),
      },
    ],
  };

  const resetForm = () => {
    reset({ ...defaultAppliedFilters });
    dispatch(
      gridStateActions.setFilterAttributes({
        ...gridState.filterAttributes,
        filterObject: Object.entries(defaultAppliedFilters),
        selectedChipFilters: {},
      }),
    );
    props.onApplyFilter(defaultAppliedFilters);
  };

  useEffect(() => {
    if (!gridStateFilters || !allCitiesandZipsData.current) return;
    const selectedStates = !(gridStateFilters?.filters?.states as any[])?.length
      ? []
      : (gridStateFilters?.filters?.states as any[]).reduce(
          (result, item) => (!item?.isCompactOpt ? [...result, item] : result),
          [],
        );

    const selectedCities = (
      !(gridStateFilters?.filters?.city as any[])?.length ? [] : gridStateFilters?.filters?.city
    ) as any[];
    updateGridState(
      selectedStates.map(x => x.name),
      selectedCities.map(x => x.name),
      gridStateFilters,
    );
  }, [gridStateFilters, updateGridState]);

  useEffect(() => {
    fetchFacilityFilterSpecs();
    resetForm();
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid item xs={10.8}>
        <Collapse timeout={200} in={filtersExpanded} collapsedSize={45} className={classes.collapse}>
          <CustomFilter
            fields={fields}
            justifyContent={'flex-start'}
            version2
            resetBtn
            disableFormSubmit
            analytics={{
              caller: t('Placement Change facility Search'),
              onApply: true,
              properties: {
                category: SearchType.facility,
              },
            }}
            onApply={() => {
              props.onApplyFilter(getValues());
            }}
          />
        </Collapse>
      </Grid>
      <Grid item xs={1.2} className={classes.fillterCollapse}>
        <SearchPageIcons
          resetlbutton
          funnelButton
          checked={filtersExpanded}
          onResetForm={() => resetForm()}
          onAdvancedFilterClick={() => handleChange()}
        />
      </Grid>
    </Grid>
  );
};
